/**
 * Cadastro em andamento, entre o formulário de dados e a escolha do plano.
 *
 * Existe porque o passo 1 já cria a conta no Supabase. Um refresh no meio do
 * caminho, sem esse estado, devolveria a pessoa ao formulário — e ali ela
 * ficaria presa, porque um segundo cadastro com o mesmo e-mail é recusado de
 * propósito (migration 0018).
 *
 * O estado é amarrado ao usuário que o criou, e é isso que faltava: o
 * `sessionStorage` dura o que a aba durar, e o logout não o limpa. Quem
 * criasse uma conta, saísse e abrisse /register de novo caía direto na escolha
 * de plano, com o e-mail da conta anterior no formulário — sem sessão para
 * pagar e sem caminho de volta óbvio.
 *
 * Guardar o `userId` e conferir contra a sessão atual resolve os dois lados:
 * quem voltou de um refresh continua de onde parou, e quem saiu recomeça do
 * início.
 */

import { supabase } from '@/lib/supabase';

const CHAVE_USUARIO = 'cc_pending_user_id';
const CHAVE_EMAIL = 'cc_pending_email';

export interface CadastroPendente {
  userId: string;
  email: string;
}

/**
 * `sessionStorage` pode lançar — modo privado, cota, políticas de storage. O
 * cadastro não pode falhar por causa da conveniência de sobreviver ao refresh,
 * então toda operação aqui é best-effort.
 */
function ler(): CadastroPendente | null {
  try {
    const userId = sessionStorage.getItem(CHAVE_USUARIO);
    const email = sessionStorage.getItem(CHAVE_EMAIL);
    return userId && email ? { userId, email } : null;
  } catch {
    return null;
  }
}

export function gravarCadastroPendente(userId: string, email: string): void {
  try {
    sessionStorage.setItem(CHAVE_USUARIO, userId);
    sessionStorage.setItem(CHAVE_EMAIL, email);
  } catch {
    /* segue sem persistir: só se perde a retomada após refresh */
  }
}

export function limparCadastroPendente(): void {
  try {
    sessionStorage.removeItem(CHAVE_USUARIO);
    sessionStorage.removeItem(CHAVE_EMAIL);
  } catch {
    /* idem */
  }
}

/** Assinatura viva: não há mais cadastro a retomar. */
const STATUS_ATIVOS = ['active', 'trialing'];

/**
 * Devolve o cadastro pendente **apenas** enquanto ele ainda faz sentido:
 * a sessão ativa é a mesma que o criou e a assinatura ainda não existe.
 *
 * Sem sessão, sessão de outra conta ou assinatura já ativa, o estado é
 * descartado e o cadastro recomeça do formulário.
 *
 * A checagem da assinatura entrou quando a tela de sucesso saiu do fluxo — era
 * ela que limpava o estado ao confirmar o pagamento. Quem paga hoje vai direto
 * ao dashboard, e sem isto o `sessionStorage` sobreviveria na aba: abrir
 * /register de novo cairia na escolha de plano, para quem já tem plano.
 *
 * A consulta só acontece quando há algo pendente — no caso comum, nenhuma.
 */
export async function restaurarCadastroPendente(): Promise<CadastroPendente | null> {
  const pendente = ler();
  if (!pendente) return null;

  const { data } = await supabase.auth.getSession();

  if (data?.session?.user?.id !== pendente.userId) {
    limparCadastroPendente();
    return null;
  }

  const { data: perfil } = await supabase
    .from('profiles')
    .select('subscription_status')
    .eq('id', pendente.userId)
    .maybeSingle();

  if (perfil && STATUS_ATIVOS.includes(perfil.subscription_status ?? '')) {
    limparCadastroPendente();
    return null;
  }

  return pendente;
}
